import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from 'lib/supabase';
import AdminDashboardLayout from '../admin-dashboard-overview/components/AdminDashboardLayout';
import SearchBar from '../admin-clients-management/components/SearchBar';
import Pagination from '../admin-clients-management/components/Pagination';
import Button from 'components/ui/Button';
import Icon from 'components/AppIcon';

const AdminPendingProfessionalsPage = () => {
  const navigate = useNavigate();
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 25;

  useEffect(() => {
    fetchPendingProfessionals();
  }, [currentPage, searchQuery]);

  const fetchPendingProfessionals = async () => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase
        ?.from('users')
        ?.select('*', { count: 'exact' })
        ?.eq('role', 'professionista')
        ?.eq('verification_status', 'pending')
        ?.order('created_at', { ascending: false });

      // Apply search filter
      if (searchQuery?.trim()) {
        query = query?.or(`email.ilike.%${searchQuery?.trim()}%,profession.ilike.%${searchQuery?.trim()}%`);
      }

      // Apply pagination
      const from = (currentPage - 1) * itemsPerPage;
      const to = from + itemsPerPage - 1;
      query = query?.range(from, to);

      const { data, error: fetchError, count } = await query;

      if (fetchError) throw fetchError;

      setProfessionals(data || []);
      setTotalCount(count || 0);
    } catch (err) {
      console.error('Error fetching pending professionals:', err);
      setError('Errore nel caricamento dei professionisti in attesa');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const handleReview = (professionalId) => {
    navigate(`/admin/professionisti/review/${professionalId}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date?.toLocaleDateString('it-IT', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  return (
    <AdminDashboardLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Professionisti in Attesa di Verifica</h1>
          <p className="text-gray-600 mt-2">Rivedi e approva i professionisti in attesa</p>
        </div>

        {/* Search Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <SearchBar onSearch={handleSearch} placeholder="Cerca per email o professione..." />
        </div>

        {/* Results Summary */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Totale: <span className="font-semibold">{totalCount}</span> professionisti in attesa
          </p>
          <p className="text-sm text-gray-600">
            Pagina {currentPage} di {totalPages || 1}
          </p>
        </div>

        {/* Table */}
        {loading ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 flex items-center justify-center">
            <div className="flex flex-col items-center gap-4">
              <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-gray-600">Caricamento...</p>
            </div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            {error}
          </div>
        ) : professionals?.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <Icon name="Briefcase" size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600">Nessun professionista in attesa di verifica</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Professione
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data Creazione
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Azioni
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {professionals?.map((prof) => (
                    <tr key={prof?.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{prof?.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600">{prof?.profession || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600">{formatDate(prof?.created_at)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => handleReview(prof?.id)}
                        >
                          Rivedi
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination */}
        {!loading && !error && totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        )}
      </div>
    </AdminDashboardLayout>
  );
};

export default AdminPendingProfessionalsPage;